import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Checkbox, Col, List, Row, Spin } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import { getGroovyFuncNamesFromStr } from '../../utils/util';

/**
 * Represents single Groovy function that can be selected by the user via
 * the ManageGroovyFuncs component.
 */
const GroovyFunctionOption = props => {
  const { groovyFunction, disabled, mode, onOptionChange } = props;
  // The function definition being edited by the user. This is updated whenever
  // the user changes the function definition
  const [editableFunctionDef, setEditableFunctionDef] = useState(groovyFunction.value);

  // Indicates if the function definition is currently being shown to the user.
  // This is toggled by clicking the Show/Hide button.
  const [functionDefVisible, setFunctionDefVisible] = useState(false);

  function onCheckboxChange(e) {
    const { checked } = e.target;
    onOptionChange(groovyFunction, checked);
  }

  const adminMode = mode === 'admin';

  const groovyFunctionName = getGroovyFuncNamesFromStr(groovyFunction.value, true);

  const cardTitleIcon = functionDefVisible === true ? <EyeInvisibleOutlined /> : <EyeOutlined />;
  const showHideLabel = functionDefVisible === true ? 'Hide' : 'Show';

  // Button to either show or hide the complete function definition.
  // When clicked, flip the visibility state of the function definition.
  const showHideButton = (
    <Button
      data-cy={`${showHideLabel}-${groovyFunctionName}`}
      type="primary"
      onClick={() => setFunctionDefVisible(!functionDefVisible)}
      icon={cardTitleIcon}
    >
      {showHideLabel}
    </Button>
  );

  let cardTitle = (
    <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
      <Col span={20}>
        <span>
          <span>
            <Checkbox
              data-cy={`checkbox-${groovyFunctionName}`}
              onChange={e => onCheckboxChange(e)}
            />
          </span>
          <span style={{ marginLeft: 12 }}>{groovyFunctionName}</span>
        </span>
      </Col>
      <Col span={4}>
        <span style={{ cursor: 'pointer', color: 'blue', marginLeft: 12 }}>{showHideButton}</span>
      </Col>
    </Row>
  );

  if (disabled === true) {
    cardTitle = <Spin indicator={null}>{cardTitle}</Spin>;
  }

  let codeMirrorContent = null;

  if (functionDefVisible === true) {
    codeMirrorContent = (
      <CodeMirror
        className="codemirror-import"
        value={editableFunctionDef}
        onChange={(_, __, value) => {
          setEditableFunctionDef(value);
        }}
        options={{
          mode: 'groovy',
          theme: 'neat',
          lineNumbers: true,
          indentUnit: 2,
          lineWrapping: true,
          readOnly: !adminMode,
        }}
      />
    );
  }

  return (
    <List.Item>
      <List.Item.Meta title={cardTitle} description={codeMirrorContent} />
    </List.Item>
  );
};

GroovyFunctionOption.propTypes = {
  /**
   * Indicates if the option is interactable for the user.
   * Currently the option is disabled if a function of the same name
   * already exists within the client function library.
   */
  disabled: PropTypes.bool.isRequired,
  /**
   * Database ID and value (content of the function) for a single function
   */
  groovyFunction: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
  /**
   * Indicates if this is an admin view or not.
   */
  mode: PropTypes.string.isRequired,
  /**
   * Function that is called when the option is either selected or deselected.
   */
  onOptionChange: PropTypes.func.isRequired,
};

export default GroovyFunctionOption;
